import type { QuestionRegion } from '@/types/pipeline';

/** Stroke colors for regions — keep in sync with ImageWithRegions fill tints where applicable */
export const BBOX_COLORS = {
  sectionInstruction: ['#c62828', '#6a1b9a', '#00695c', '#e65100'],
  sectionContent: ['#5d4037', '#37474f', '#4e342e', '#455a64'],
  question: ['#1e88e5', '#43a047', '#fb8c00', '#8e24aa', '#00acc1', '#ffb300', '#6d4c41', '#d81b60', '#3949ab', '#e53935'],
  answerText: '#2e7d32',
  answerMc: '#1565c0',
  answerSymbol: '#f57c00',
  answerDrawing: '#7b1fa2',
} as const;

const HIGHLIGHT_STROKE = '#f44336'; // Material Design Red 500 - distinct highlight color

function colorForRegion(region: QuestionRegion, colorIndices: { instruction: number; content: number; question: number }): string {
  switch (region.type) {
    case 'section-instruction': {
      const idx = colorIndices.instruction % BBOX_COLORS.sectionInstruction.length;
      colorIndices.instruction++;
      return BBOX_COLORS.sectionInstruction[idx] ?? '#c62828';
    }
    case 'section-content': {
      const idx = colorIndices.content % BBOX_COLORS.sectionContent.length;
      colorIndices.content++;
      return BBOX_COLORS.sectionContent[idx] ?? '#5d4037';
    }
    case 'answer-text':
      return BBOX_COLORS.answerText;
    case 'answer-mc':
      return BBOX_COLORS.answerMc;
    case 'answer-symbol':
      return BBOX_COLORS.answerSymbol;
    case 'answer-drawing':
      return BBOX_COLORS.answerDrawing;
    default: {
      const idx = colorIndices.question % BBOX_COLORS.question.length;
      colorIndices.question++;
      return BBOX_COLORS.question[idx] ?? '#1e88e5';
    }
  }
}

/**
 * Same stroke palette order as ImageWithRegions for a given ordered region list (e.g. one page).
 */
export function getRegionDisplayColors(regions: QuestionRegion[]): string[] {
  const colorIndices = { instruction: 0, content: 0, question: 0 };
  return regions.map((region) => colorForRegion(region, colorIndices));
}

/**
 * Stroke color for the rubric row that matches the overlay box for this questionId.
 * Uses the page-local region list and the same cycling rules as the image overlay.
 */
export function getQuestionRubricAccentColor(allRegions: QuestionRegion[], questionId: string): string | null {
  const qIdx = allRegions.findIndex(
    (r) => r.questionId === questionId && (r.type === 'question' || r.type === 'mc-question')
  );
  const aIdx =
    qIdx >= 0
      ? -1
      : allRegions.findIndex((r) => r.parentQuestion === questionId && (r.type?.startsWith('answer-') ?? false));

  const ref = allRegions[qIdx >= 0 ? qIdx : aIdx];
  if (!ref) return null;

  const page = ref.pageIndex ?? 0;
  const pageRegs = allRegions.filter((r) => (r.pageIndex ?? 0) === page);
  const colors = getRegionDisplayColors(pageRegs);

  const rowIdx =
    qIdx >= 0
      ? pageRegs.findIndex((r) => r.questionId === questionId && (r.type === 'question' || r.type === 'mc-question'))
      : pageRegs.findIndex((r) => r.parentQuestion === questionId && (r.type?.startsWith('answer-') ?? false));

  if (rowIdx < 0 || rowIdx >= colors.length) return null;
  return colors[rowIdx] ?? null;
}

export { HIGHLIGHT_STROKE };
